import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconDownload, IconEdit, IconTrash, IconView } from '../components/icons';
import Pagination from '../components/Pagination';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { downloadListCsv } from '../utils/csv';
import { formatDateShort } from '../utils/format';
import usePaginatedList from '../hooks/usePaginatedList';
import useCsvExport from '../hooks/useCsvExport';

// TS port of the source (Falah) app's pages/Events.jsx — ported as-is
// (generic module). Not permission-gated: `/events` is open to any
// authenticated user (see routes/api.php).
interface EventRow {
  id: number;
  title: string;
  description: string | null;
  start_at: string;
  end_at: string | null;
  is_public_holiday: boolean;
}

export function Events() {
  const confirmDelete = useConfirm();
  const { showError } = useToast();

  const filters = useMemo(() => ({}), []);
  const { data, loading, error, setPage, reload: load } = usePaginatedList<EventRow>('/events', filters);

  const remove = async (event: EventRow) => {
    if (!(await confirmDelete(`Delete "${event.title}"?`))) return;
    try {
      await api.delete(`/events/${event.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const { exporting, exportCsv } = useCsvExport(() =>
    downloadListCsv<EventRow>(
      api,
      '/events',
      {},
      [
        { label: 'Title', value: (r) => r.title },
        { label: 'Description', value: (r) => r.description },
        { label: 'Start', value: (r) => r.start_at },
        { label: 'End', value: (r) => r.end_at },
        { label: 'Public Holiday', value: (r) => (r.is_public_holiday ? 'Yes' : 'No') },
      ],
      'events.csv'
    )
  );

  return (
    <>
      <header className="page-header">
        <h1>Events</h1>
        <Link className="btn btn-primary" to="/events/new">
          + Add
        </Link>
      </header>

      <div className="toolbar">
        <IconButton
          icon={IconDownload}
          label={exporting ? 'Exporting…' : 'Download CSV'}
          onClick={exportCsv}
          disabled={exporting}
        />
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Event</th>
                <th>When</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={3} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={3} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((ev) => (
                  <tr key={ev.id}>
                    <td>
                      <strong>{ev.title}</strong>
                      {ev.is_public_holiday && <span className="badge badge-active"> Public Holiday</span>}
                      {ev.description && (
                        <div>
                          <small className="muted">{ev.description}</small>
                        </div>
                      )}
                    </td>
                    <td>
                      {formatDateShort(ev.start_at)}
                      {ev.end_at && ev.end_at !== ev.start_at && <small className="muted"> → {formatDateShort(ev.end_at)}</small>}
                    </td>
                    <td className="right">
                      <IconButton icon={IconView} label="View" to={`/events/${ev.id}`} />
                      <IconButton icon={IconEdit} label="Edit" to={`/events/${ev.id}/edit`} />
                      <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(ev)} />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="muted center">
                    No events yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>
    </>
  );
}
